import React, { useEffect, useState } from "react";
import {
	Box,
	Flex,
	Text,
	Input,
	InputGroup,
	InputLeftElement,
	Checkbox,
	VStack,
	Spinner,
	IconButton,
	Button,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	HStack,
	Icon
} from "@chakra-ui/react";
import { FiSearch, FiTrash2, FiPlus, FiX, FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { fetchListOfChatSessions, deleteChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import showToast from "hooks/useCustomToast";
import { DarwinHeader, SortBy, SortOrder } from "build-ts/common";
import { ChatClientType } from "build-ts/chat_session";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { Header } from "build-ts/metadata";
import { useAnalystChat } from "context/AnalystChatContext";
import { BsRobot } from "react-icons/bs";
import { ChevronDownIcon } from "@chakra-ui/icons";

const ChatSessionPage: React.FC = () => {
	const navigate = useNavigate();
	const { defaultTimeZone } = useUser();
	const { analystsList, chatSessionsList, setChatSessionsList } = useAnalystChat();

	// State
	const [chatSessions, setChatSessions] = useState<DarwinHeader[]>([]);
	const [totalChats, setTotalChats] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [hoveredId, setHoveredId] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Analyst filter state
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);

	// Inline rename state
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editValue, setEditValue] = useState("");

	useEffect(() => {
		if (chatSessionsList && chatSessionsList.length > 0) {
			setChatSessions(chatSessionsList);
			setTotalChats(chatSessionsList.length);
			setIsLoading(false);
		}
	}, [chatSessionsList]);

	const fetchChatSessions = async (search?: string, analystFilter?: Header | null, offset: number = 0, append: boolean = false) => {
		if (append) {
			setIsLoadingMore(true);
		} else {
			setIsLoading(true);
		}
		try {
			// Build associatedEntityKey if analyst is selected
			const associatedEntityKey = analystFilter ? [`ANALYST::${analystFilter.id}`] : [];

			const result = await fetchListOfChatSessions(
				[ChatClientType.FRIDA],
				50,
				offset,
				search,
				undefined,
				associatedEntityKey,
				SortBy.LAST_MODIFIED_DATE,
				SortOrder.DESC
			);

			if (append) {
				setChatSessions(prev => [...prev, ...result.headers]);
				setTotalChats(prev => prev + result.headers.length);
			} else {
				setChatSessions(result.headers);
				setChatSessionsList(result.headers);
				setTotalChats(result.total ?? result.headers.length);
			}
		} catch (error) {
			console.error("Failed to fetch chat sessions:", error);
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	};

	// Load more chat sessions
	const handleLoadMore = () => {
		fetchChatSessions(searchQuery || undefined, selectedAnalyst, chatSessions.length, true);
	};

	// Handle rename
	const handleRename = async (sessionId: string) => {
		if (!editValue.trim()) {
			setEditingId(null);
			return;
		}
		const success = await updateChatSessionHeaders([sessionId], editValue.trim(), undefined, false);
		if (success) {
			showToast("Chat renamed", "", "success");
			fetchChatSessions(searchQuery || undefined, selectedAnalyst);
		}
		setEditingId(null);
	};

	// Handle analyst selection
	const handleAnalystSelect = (analyst: Header | null) => {
		setSelectedAnalyst(analyst);
		fetchChatSessions(searchQuery || undefined, analyst);
	};

	// Handle search on Enter key press
	const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			fetchChatSessions(searchQuery || undefined, selectedAnalyst);
		}
	};

	// Format date using timezone from user context
	const formatLastModified = (timestamp?: number): string => {
		if (!timestamp) return "";
		return epochConverterWithTimeZone(timestamp, "d-MMM-yyyy, HH:mm", defaultTimeZone) + " " + defaultTimeZone.toUpperCase();
	};

	// Handle selection
	const toggleSelection = (id: string) => {
		setSelectedIds((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	const toggleSelectAll = () => {
		if (selectedIds.size === chatSessions.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(chatSessions.map((s) => s.id!).filter(Boolean)));
		}
	};

	// Handle delete
	const handleDeleteSelected = async () => {
		if (selectedIds.size === 0) return;

		setIsDeleting(true);
		try {
			await Promise.all(
				Array.from(selectedIds).map((id) => deleteChatSession(id))
			);
			setSelectedIds(new Set());
			fetchChatSessions(searchQuery || undefined, selectedAnalyst);
		} catch (error) {
			console.error("Failed to delete chat sessions:", error);
		} finally {
			setIsDeleting(false);
		}
	};

	// Handle chat click - open in new tab
	const handleChatClick = (id: string) => {
		window.open(`/chat/${id}`, "_blank");
	};

	const isAllSelected = chatSessions.length > 0 && selectedIds.size === chatSessions.length;

	return (
		<Box
			h="100vh"
			w="100%"
			bg="white"
			overflow="hidden"
		>
			<Flex h="100%" w="100%" justify="center">
				{/* Main Content */}
				<Box w="100%" maxW="600px" h="100%" px={5} py={6}>
					{/* Header */}
					<Flex justify="space-between" align="center" mb={10} mt={8}>
						<Text
							fontSize="2xl"
							fontWeight="700"
							color="purple.900"
							m={0}
						>
							Chats
						</Text>
						<Button
							leftIcon={<Icon as={FiPlus} boxSize={5} />}
							bg="purple.700"
							color="white"
							size="md"
							fontWeight="600"
							borderRadius="md"
							_hover={{ bg: "purple.600" }}
							onClick={() => navigate("/chat")}
						>
							New chat
						</Button>
					</Flex>

					{/* Search Bar */}
					<InputGroup mb={5} size="md">
						<InputLeftElement pointerEvents="none" h="40px">
							<Icon as={FiSearch} boxSize={5} color="purple.700" />
						</InputLeftElement>
						<Input
							placeholder="Search chats..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={handleSearchKeyDown}
							bg="white"
							border="1px solid"
							borderColor="purple.800"
							borderRadius="lg"
							fontSize="md"
							color="fg.default"
							_placeholder={{ color: "purple.800" }}
							_focus={{
								borderColor: "purple.700",
								boxShadow: "none",
								borderWidth: "2px",
								bg: "purple.50",
							}}
						/>
					</InputGroup>

					{selectedAnalyst && (
						<HStack
							bg="purple.50"
							px={2}
							py={2}
							mb={2}
							borderRadius="md"
							spacing={1}
							justify="space-between"
						>
							<HStack spacing={2}>
								<Icon as={BsRobot} boxSize={5} />
								<Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
									{selectedAnalyst.name}
								</Text>
							</HStack>

							<IconButton
								aria-label="Clear filter"
								icon={<Icon as={FiX} boxSize={4} />}
								size="xs"
								variant="ghost"
								color="purple.600"
								minW="auto"
								h="auto"
								p={0.5}
								_hover={{ color: "purple.800" }}
								onClick={() => handleAnalystSelect(null)}
							/>
						</HStack>
					)}

					{/* Selection Actions */}
					{selectedIds.size > 0 && (
						<Flex
							mb={2}
							pl={3}
							pr={1}
							py={0}
							bg="gray.100"
							borderRadius="md"
							align="center"
							justify="space-between"
						>
							<Text fontSize="sm" color="fg.default" fontWeight="600" align="left" m={0}>
								{selectedIds.size} chat{selectedIds.size > 1 ? "s" : ""} selected
							</Text>
							<Button
								leftIcon={<Icon as={FiTrash2} boxSize={4} />}
								size="xs"
								colorScheme="red"
								variant="ghost"
								isLoading={isDeleting}
								onClick={handleDeleteSelected}
								fontWeight="700"
							>
								Delete
							</Button>
						</Flex>
					)}

					{/* Chat Count & Select All & Filter */}
					<Flex align="center" justify="space-between" mb={3} px={1}>
						<HStack spacing={2}>
							<Checkbox
								isChecked={isAllSelected}
								onChange={toggleSelectAll}
								colorScheme="gray"
								size="md"
								m={0}
							/>
							<Text fontSize="sm" color="gray.500" fontWeight="600" align="left" m={0}>
								Showing {totalChats} chat{totalChats !== 1 ? "s" : ""}
							</Text>
						</HStack>

						{/* Analyst Filter */}
						<HStack spacing={2}>
							<Menu>
								<MenuButton
									as={Button}
									size="sm"
									variant="outline"
									colorScheme="purple"
									bg="purple.50"
									borderColor="purple.50"
									_hover={{
										bg: "purple.100",
										borderColor: "purple.100"
									}}
									borderRadius="full"
									borderWidth="1px"
									px={3}
									py={1}
								>
									<HStack spacing={1}>
										<Icon as={BsRobot} boxSize={5} />
										<Icon as={ChevronDownIcon} boxSize={5} />
									</HStack>
								</MenuButton>
								<MenuList
									maxH="250px"
									overflowY="auto"
									minW="200px"
									borderColor="purple.200"
									boxShadow="lg"
								>
									{analystsList.length === 0 ? (
										<MenuItem isDisabled fontSize="sm" color="purple.500">
											No analysts found
										</MenuItem>
									) : (
										analystsList.map((analyst) => (
											<MenuItem
												key={analyst.id}
												fontSize="sm"
												bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
												_hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
												onClick={() => handleAnalystSelect(analyst)}
											>
												<HStack spacing={2}>
													<Icon as={BsRobot} boxSize={5} color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"} />
													<Text fontSize="sm" color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"} fontWeight="500" m={0} noOfLines={1}>
														{analyst.name}
													</Text>
												</HStack>
											</MenuItem>
										))
									)}
								</MenuList>
							</Menu>
						</HStack>
					</Flex>

					{/* Chat List */}
					<Box
						h="calc(100vh - 200px)"
						maxH="600px"
						overflowY="auto"
						css={{
							"&::-webkit-scrollbar": {
								width: "4px",
							},
							"&::-webkit-scrollbar-track": {
								background: "transparent",
							},
							"&::-webkit-scrollbar-thumb": {
								background: "#dfc2f0",
								borderRadius: "2px",
							},
						}}
					>
						{isLoading ? (
							<Flex justify="center" align="center" h="150px">
								<Spinner size="md" color="purple.700" thickness="2px" />
							</Flex>
						) : chatSessions.length === 0 ? (
							<Flex
								direction="column"
								align="center"
								justify="center"
								h="150px"
								color="gray.500"
							>
								<Text fontSize="md" mb={1}>
									{searchQuery ? "No chats found" : "No chats yet"}
								</Text>
								<Text fontSize="sm">
									{searchQuery
										? "Try a different search term"
										: "Start a new chat to begin"}
								</Text>
							</Flex>
						) : (
							<VStack spacing={2} align="stretch">
								{chatSessions.map((session) => {
									const isEditing = editingId === session.id;
									return (
										<Flex
											key={session.id}
											p={3}
											align="center"
											borderRadius="lg"
											border={selectedIds.has(session.id!) ? "2px solid" : "1px solid"}
											borderColor={selectedIds.has(session.id!) ? "gray.500" : "gray.300"}
											bg={
												selectedIds.has(session.id!)
													? "purple.50"
													: hoveredId === session.id
														? "gray.50"
														: "white"
											}
											cursor={isEditing ? "default" : "pointer"}
											transition="all 0.15s ease"
											onMouseEnter={() => setHoveredId(session.id!)}
											onMouseLeave={() => setHoveredId(null)}
											onClick={() => !isEditing && handleChatClick(session.id!)}
											role="group"
											tabIndex={0}
											_hover={{ borderColor: "gray.500" }}
										>
											{/* Checkbox */}
											<Box
												onClick={(e) => {
													e.stopPropagation();
													toggleSelection(session.id!);
												}}
												mr={3}
											>
												<Checkbox
													isChecked={selectedIds.has(session.id!)}
													onChange={() => toggleSelection(session.id!)}
													colorScheme="purple"
													size="md"
												/>
											</Box>

											{/* Chat Info */}
											<Box flex="1" minW={0}>
												{isEditing ? (
													<Input
														size="sm"
														autoFocus
														value={editValue}
														onChange={(e) => setEditValue(e.target.value)}
														onKeyDown={(e) => {
															if (e.key === "Enter") handleRename(session.id!);
															if (e.key === "Escape") setEditingId(null);
														}}
														onBlur={() => setEditingId(null)}
														onClick={(e) => e.stopPropagation()}
														bg="white"
														color="fg.default"
														border="1px solid"
														borderColor="gray.300"
														borderRadius="md"
														fontSize="sm"
													/>
												) : (
													<>
														<Text
															fontSize="sm"
															fontWeight="600"
															color="fg.default"
															noOfLines={1}
															align="left"
															m={0}
														>
															{session.name || "Untitled"}
														</Text>
														<Text fontSize="xs" color="gray.500" align="left" m={0} mt={1}>
															{formatLastModified(session.last_modified_date)}
														</Text>
													</>
												)}
											</Box>

											{/* Actions Menu */}
											{!isEditing && (
												<Menu placement="bottom-start">
													<MenuButton
														as={IconButton}
														icon={<Icon as={ChevronDownIcon} boxSize={5} />}
														size="xs"
														variant="ghost"
														color="purple.700"
														_hover={{ bg: "purple.700", color: "white" }}
														aria-label="Chat options"
														onClick={(e) => e.stopPropagation()}
														opacity={0}
														_groupHover={{ opacity: 1 }}
														transition="opacity 0.2s"
													/>
													<MenuList minW="120px" boxShadow="lg" zIndex={1001}>
														<MenuItem
															icon={<FiEdit2 size={14} />}
															fontSize="sm"
															onClick={(e) => {
																e.stopPropagation();
																setEditValue(session.name || "");
																setEditingId(session.id!);
															}}
														>
															Rename
														</MenuItem>
														<MenuItem
															icon={<FiTrash2 size={14} />}
															fontSize="sm"
															color="red.500"
															onClick={(e) => {
																e.stopPropagation();
																deleteChatSession(session.id!).then(() => {
																	fetchChatSessions(searchQuery || undefined, selectedAnalyst);
																});
															}}
														>
															Delete
														</MenuItem>
													</MenuList>
												</Menu>
											)}
										</Flex>
									);
								})}

								{/* Load More Button - show when count is multiple of 50 */}
								{chatSessions.length > 0 && chatSessions.length % 50 === 0 && (
									<Button
										variant="outline"
										colorScheme="purple"
										size="sm"
										mt={2}
										isLoading={isLoadingMore}
										loadingText="Loading..."
										onClick={handleLoadMore}
									>
										Load More
									</Button>
								)}
							</VStack>
						)}
					</Box>
				</Box>
			</Flex>
		</Box>
	);
};

export default ChatSessionPage;
